import React from 'react';
import { Box } from '@mui/material';

import TimelineIcon from '@mui/icons-material/Timeline';
import SpeedIcon from '@mui/icons-material/Speed';
import GroupsIcon from '@mui/icons-material/Groups';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import PivotTableChartIcon from '@mui/icons-material/PivotTableChart';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import MemoryIcon from '@mui/icons-material/Memory';

const ACCENT = '#10b981';

const layerIcons = {
    layer2: [<TimelineIcon />, <GroupsIcon />],
    layer3: [<SpeedIcon />, <DonutLargeIcon />, <PivotTableChartIcon />, <ScatterPlotIcon />],
    layer4: Array.from({ length: 16 }, (_, i) => <MemoryIcon key={i} />),
};

const SoccerFieldSVG = () => (
    <svg
        viewBox="0 0 100 140"
        style={{
            width: '54%',
            height: '70%',
            opacity: 0.98,
            display: 'block',
            filter: 'drop-shadow(0 0 12px rgba(16,185,129,0.55))',
        }}
    >
        <rect x="2" y="2" width="96" height="136" fill="none" stroke="#fff" strokeWidth="2.6" />
        <line x1="2" y1="70" x2="98" y2="70" stroke="#fff" strokeWidth="2.6" />
        <circle cx="50" cy="70" r="12" fill="none" stroke="#fff" strokeWidth="2.6" />
        <rect x="25" y="2" width="50" height="20" fill="none" stroke="#fff" strokeWidth="2.6" />
        <rect x="25" y="118" width="50" height="20" fill="none" stroke="#fff" strokeWidth="2.6" />
    </svg>
);

const GlassTile = ({ width, height, variant = 'base', content }) => {
    // MAIS contraste entre camadas:
    // top bem sólido, mid menos transparente, base fantasma
    const preset =
        variant === 'top'
            ? {
                face: 'rgba(16, 185, 129, 0.85)',            // << mais sólido
                middle: 'rgba(4, 90, 61, 0.85)',
                border: 'rgba(16, 185, 129, 0.25)',
                text: 'rgba(255,255,255,0.92)',
                shadow:
                    '0 26px 70px rgba(0,0,0,0.52), 0 0 44px rgba(16, 185, 129, 0.42), inset 0 0 30px rgba(16,185,129,0.16)',
                blur: 'blur(0px)',
                opacity: 1,
            }
            : variant === 'mid2' // camada 2 (2 blocos) — mais presente
                ? {
                    face: 'rgba(82, 82, 82, 0.5)',         // << sobe bem
                    middle: 'rgba(37, 37, 37, 0.50)',
                    border: 'rgba(255, 255, 255, 0.2)',
                    text: 'rgba(226,232,240,0.72)',
                    shadow: '0 20px 56px rgba(0,0,0,0.38)',
                    blur: 'blur(0.0px)',
                    opacity: 0.92,
                }
                : variant === 'mid4' // camada 3 (4 blocos) — intermediária
                    ? {
                        face: 'rgba(104, 104, 104, 0.25)',       // << sobe um degrau
                        middle: 'rgba(29, 29, 29, 0.25)',
                        border: 'rgba(255,255,255,0.2)',
                        text: 'rgba(226,232,240,0.58)',
                        shadow: '0 16px 46px rgba(0,0,0,0.34)',
                        blur: 'blur(0.2px)',
                        opacity: 0.50,
                    }
                    : {
                        face: 'rgba(120,120,120,0.10)',       // mantém base fantasma
                        middle: 'rgba(53, 53, 53, 0.10)',
                        border: 'rgba(255,255,255,0.2)',
                        text: 'rgba(226,232,240,0.58)',
                        shadow: '0 10px 28px rgba(0,0,0,0.26)',
                        blur: 'blur(0.7px)',
                        opacity: 0.15,
                    };

    return (
        <Box
            sx={{
                width,
                height,
                position: 'relative',
                borderRadius: 3,
                overflow: 'hidden',
                background: `linear-gradient(135deg, ${preset.face} 0%, ${preset.middle} 45%, ${preset.face} 100%)`,
                border: `1px solid ${preset.border}`,
                boxShadow: preset.shadow,
                opacity: preset.opacity,
                filter: preset.blur,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background:
                        variant === 'top'
                            ? 'radial-gradient(700px 260px at 26% 18%, rgba(16,185,129,0.30), transparent 62%)'
                            : 'radial-gradient(700px 260px at 26% 18%, rgba(255,255,255,0.12), transparent 62%)',
                    pointerEvents: 'none',
                },
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    color: preset.text,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                }}
            >
                {content}
            </Box>
        </Box>
    );
};

const LayerGrid = ({ z, fragments }) => {
    const size = 320;
    const gap = fragments === 16 ? 14 : 18;

    let blocks = [];
    let variant = 'base';

    if (fragments === 1) variant = 'top';
    else if (fragments === 2) variant = 'mid2';
    else if (fragments === 4) variant = 'mid4';
    else variant = 'base';

    if (fragments === 1) {
        blocks = [
            <Box key="topWrap" sx={{ width: '100%', height: '100%', p: 1.25 }}>
                <GlassTile width="100%" height="100%" variant="top" content={<SoccerFieldSVG />} />
            </Box>,
        ];
    } else if (fragments === 2) {
        const w = `calc(50% - ${gap / 2}px)`;
        blocks = layerIcons.layer2.map((icon, i) => (
            <GlassTile
                key={i}
                width={w}
                height="100%"
                variant={variant}
                content={React.cloneElement(icon, { sx: { fontSize: 56 } })}
            />
        ));
    } else if (fragments === 4) {
        const s = `calc(50% - ${gap / 2}px)`;
        blocks = layerIcons.layer3.map((icon, i) => (
            <GlassTile
                key={i}
                width={s}
                height={s}
                variant={variant}
                content={React.cloneElement(icon, { sx: { fontSize: 34 } })}
            />
        ));
    } else if (fragments === 16) {
        const s = `calc(25% - ${(gap * 3) / 4}px)`;
        blocks = layerIcons.layer4.map((icon, i) => (
            <GlassTile
                key={i}
                width={s}
                height={s}
                variant={variant}
                content={React.cloneElement(icon, { sx: { fontSize: 14 } })}
            />
        ));
    }

    return (
        <Box
            sx={{
                position: 'absolute',
                width: `${size}px`,
                height: `${size}px`,
                top: '50%',
                left: '50%',
                transformStyle: 'preserve-3d',
                transform: `translate(-50%, -50%) translateZ(${z}px)`,
                display: 'flex',
                flexWrap: 'wrap',
                gap: `${gap}px`,
                pointerEvents: 'none',
            }}
        >
            <Box sx={{ display: 'contents' }}>{blocks}</Box>
        </Box>
    );
};

export default function DeepTacticalStack() {
    return (
        <Box
            sx={{
                width: '100%',
                height: 520,
                bgcolor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'visible',
                perspective: '1400px',
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: 360,
                    height: 360,
                    transformStyle: 'preserve-3d',
                    transform: 'rotateX(62deg) rotateZ(-32deg)',
                }}
            >
                {/* sombra controlada */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: 320,
                        height: 240,
                        transform: 'translate(-50%, -50%) translateZ(-150px)',
                        bgcolor: 'rgba(0,0,0,0.60)',
                        filter: 'blur(44px)',
                        opacity: 0.42,
                        borderRadius: 999,
                        pointerEvents: 'none',
                    }}
                />

                {/* base fantasma */}
                <LayerGrid z={-105} fragments={16} />
                <LayerGrid z={-35} fragments={4} />
                <LayerGrid z={35} fragments={2} />
                <LayerGrid z={105} fragments={1} />
            </Box>
        </Box>
    );
}
